export type BrandLevel = 'none' | 'colour' | 'full'

export interface Chapter {
  snak: 1 | 2 | 3
  label: string
  archetype: string
  verb: string
  differentiation: string
  audience: string
  goal: string
  brand: BrandLevel
  brandNote: string
}

export const chapters: Chapter[] = [
  {
    snak: 1,
    label: 'Snak 1',
    archetype: 'Foundational',
    verb: 'Spark precision',
    differentiation: 'Preserve',
    audience: 'Treats DME by fluid volume. Has not yet questioned the one-size-fits-all approach.',
    goal: 'Establish that DME is not one morphological pattern.',
    brand: 'none',
    brandNote: 'Completely unbranded',
  },
  {
    snak: 2,
    label: 'Snak 2',
    archetype: 'Developing',
    verb: 'Embed precision',
    differentiation: 'Prevent',
    audience: 'Accepts that phenotypes exist. Has not yet made them part of the routine.',
    goal: 'Make phenotyping operational — and introduce the window of opportunity.',
    brand: 'colour',
    brandNote: 'Brand colours, no logo',
  },
  {
    snak: 3,
    label: 'Snak 3',
    archetype: 'Advanced',
    verb: 'Ignite precision',
    differentiation: 'Predict',
    audience: 'Already practises precision medicine. Wants infrastructure, not persuasion.',
    goal: 'Move from characterising to piloting and predicting.',
    brand: 'full',
    brandNote: 'Fully branded',
  },
]

export function chapterFor(snak: number): Chapter {
  return chapters.find((c) => c.snak === snak) ?? chapters[0]
}

export const brandLogo = {
  src: 'https://cdn.magicpatterns.com/uploads/2oBPzTf8yTj7VjZGNcPz54/Screenshot_2026-08-19_at_14.28.28.png',
  alt: 'Ozurdex (dexamethasone intravitreal implant) 0.7 mg',
}

export const entry = {
  eyebrow: 'Precision medicine adoption · Denmark',
  headline: ['Three instalments.', 'One argument.'],
  lead: 'From revealing the phenotype, through sharper diagnosis, to precision treatment — each instalment matched to where the specialist already sits.',
  openLabel: 'Choose an instalment',
  openDetail: 'Three campaigns, or one presentation',
  journeyLabel: 'The journey',
  storyboardLabel: 'Storyboard',
  menuTitle: 'Where does this specialist sit?',
  backLabel: 'Back',
  campaignLabel: 'Run one instalment',
  presentationLabel: 'Run all three',
  presentationDetail: '15 screens · face-to-face presentation mode',
  footnote:
    'HCP archetypes after the AbbVie precision-medicine adoption model: Foundational, Developing, Advanced.',
}

export interface JourneyStage {
  snak: 1 | 2 | 3
  arc: string
  title: string
  subtitle: string
  /** Weight of pure scientific content, 0–100. */
  science: number
  /** Brand presence, 0–100. */
  brandWeight: number
  trust: string
  register: string
  visual: string
  ask: string
  palette: string[]
}

export const journeyStages: JourneyStage[] = [
  {
    snak: 1,
    arc: 'Reveal',
    title: 'Reveal the phenotype',
    subtitle: 'DME is not one morphological pattern',
    science: 100,
    brandWeight: 0,
    trust: 'Trust has to be earned before anything at all is claimed.',
    register: 'Pure science. Published data, no product, no voice of a company.',
    visual: 'Unbranded. Near-monochrome, clinical OCT imagery, black on white.',
    ask: 'Book a phenotyping walkthrough',
    palette: ['#0B2A4A', '#1E76CF', '#F2F8F9'],
  },
  {
    snak: 2,
    arc: 'Enhance',
    title: 'Enhance the diagnosis',
    subtitle: 'The window closes quietly — make phenotyping routine',
    science: 65,
    brandWeight: 45,
    trust: 'Credibility is established; it now has to become useful in clinic.',
    register: 'Applied science. Timing, review rhythms, documentation.',
    visual: 'Brand colours, no logo. Warmer surface, colour used to structure.',
    ask: 'Book a proactive-management case review',
    palette: ['#203864', '#4C7C9B', '#EEF3F6'],
  },
  {
    snak: 3,
    arc: 'Prescribe',
    title: 'Precision treatment',
    subtitle: 'Pilot it, then predict it',
    science: 40,
    brandWeight: 100,
    trust: 'Trust converts into a treatment conversation and a shared project.',
    register: 'Product and partnership. Named therapy, openly aspirational science.',
    visual: 'Fully branded. Dark navy canvas, full-bleed imagery, green accents, logo present.',
    ask: 'Join the pilot · prescribe-level discussion',
    palette: ['#203864', '#A6BE39', '#4C7C9B'],
  },
]

export interface StoryboardFrame {
  /** 0 = navigation pages that sit outside the instalments. */
  id: string
  snak: 0 | 1 | 2 | 3
  title: string
  purpose: string
  /** Optional engagement layer or state rather than a main page. */
  layer?: string
}

export const storyboardFrames: StoryboardFrame[] = [
  {
    id: 'entry-cover',
    snak: 0,
    title: 'Entry — cover',
    purpose: 'The menu stays behind one button. Journey and storyboard available here.',
  },
  {
    id: 'entry-menu',
    snak: 0,
    title: 'Entry — instalment menu',
    purpose: 'Three archetypes with audience and branding level, or run all three in sequence.',
  },
  {
    id: 'promise',
    snak: 1,
    title: 'Reframe the OCT',
    purpose: 'Challenge the frame without criticising current practice.',
  },
  {
    id: 'whyDiffer',
    snak: 1,
    title: 'Establish heterogeneity',
    purpose: 'Same diagnosis, different phenotype. Optional quiz layer sits here.',
  },
  {
    id: 's1-quiz-question',
    snak: 1,
    title: 'Quiz — the question',
    purpose: 'Opened from the quiz button. Asks the unmet-need question before any data.',
    layer: 'Hidden layer',
  },
  {
    id: 's1-quiz-result',
    snak: 1,
    title: 'Quiz — the answer',
    purpose: 'Peer comparison, the 31–46% dot grid and the bridge back to the science.',
    layer: 'Hidden layer',
  },
  {
    id: 'inputs',
    snak: 1,
    title: 'Explain the inputs',
    purpose: 'Five familiar variables make the AI output credible rather than opaque.',
  },
  {
    id: 'phenotypes-cl1',
    snak: 1,
    title: 'The reveal — Cluster 1',
    purpose: 'Early pattern: mainly central IRF, no SRF, intact ELM/EZ, lower I-HRF.',
    layer: 'Cluster 1 of 4',
  },
  {
    id: 'phenotypes-cl2',
    snak: 1,
    title: 'The reveal — Cluster 2',
    purpose: 'Intermediate: peripheral IRF, no SRF, intact bands, increased I-HRF.',
    layer: 'Cluster 2 of 4',
  },
  {
    id: 'phenotypes-cl3',
    snak: 1,
    title: 'The reveal — Cluster 3',
    purpose: 'Advanced: central and peripheral IRF, SRF, EZ disruption, higher I-HRF.',
    layer: 'Cluster 3 of 4',
  },
  {
    id: 'phenotypes-cl4',
    snak: 1,
    title: 'The reveal — Cluster 4',
    purpose: 'More advanced: greater SRF with ELM and EZ disruption, higher I-HRF.',
    layer: 'Cluster 4 of 4',
  },
  {
    id: 'bridge',
    snak: 1,
    title: 'Scientific next step',
    purpose: 'Low-friction ask: a phenotyping walkthrough, not a product meeting.',
  },
  {
    id: 's2-window',
    snak: 2,
    title: 'The window of opportunity',
    purpose: 'Introduce the time axis — structure changes before function complains.',
  },
  {
    id: 's2-irreversible',
    snak: 2,
    title: 'What does not come back',
    purpose: 'Fluid versus architecture. Bayesian prior-evidence-update probe here.',
  },
  {
    id: 's2-probe-prior',
    snak: 2,
    title: 'Probe 1 — state your prior',
    purpose: 'How often would you expect interrupted outer retinal bands? Answered blind.',
    layer: 'Bayesian probe',
  },
  {
    id: 's2-probe-evidence',
    snak: 2,
    title: 'Probe 2 — the evidence',
    purpose: 'Prior echoed back, then the cluster evidence that speaks to it.',
    layer: 'Bayesian probe',
  },
  {
    id: 's2-probe-update',
    snak: 2,
    title: 'Probe 3 — update',
    purpose: 'Updated view against peer responses, and what the phenotype actually changed.',
    layer: 'Bayesian probe',
  },
  {
    id: 's2-proactive-cl1',
    snak: 2,
    title: 'Review rhythm — Cluster 1',
    purpose: 'Watch central IRF and band continuity. Review at 3 months.',
    layer: 'Cluster 1 of 4',
  },
  {
    id: 's2-proactive-cl2',
    snak: 2,
    title: 'Review rhythm — Cluster 2',
    purpose: 'Watch I-HRF burden and peripheral spread. Review at 2–3 months.',
    layer: 'Cluster 2 of 4',
  },
  {
    id: 's2-proactive-cl3',
    snak: 2,
    title: 'Review rhythm — Cluster 3',
    purpose: 'Watch EZ continuity and SRF persistence. Review at 6–8 weeks.',
    layer: 'Cluster 3 of 4',
  },
  {
    id: 's2-proactive-cl4',
    snak: 2,
    title: 'Review rhythm — Cluster 4',
    purpose: 'Watch extent of band loss and response. Review at 4–6 weeks.',
    layer: 'Cluster 4 of 4',
  },
  {
    id: 's2-embed',
    snak: 2,
    title: 'Embed it in the read',
    purpose: 'Four habits inside the existing read. What changes on Tuesday.',
  },
  {
    id: 's2-prevent',
    snak: 2,
    title: 'Differentiation · Prevent',
    purpose: 'Brand colours, no logo. Timing argument becomes the product argument.',
  },
  {
    id: 's3-principles',
    snak: 3,
    title: 'Precision medicine principles',
    purpose: 'Concede they already do this; name instrumentation as the gap.',
  },
  {
    id: 's3-fit',
    snak: 3,
    title: 'AI fit-to-clinic',
    purpose: 'Honest does / does-not scope. Bayesian probe on reader variability.',
  },
  {
    id: 's3-probe-prior',
    snak: 3,
    title: 'Probe 1 — state your prior',
    purpose: 'How closely do two readers agree counting hyperreflective foci? Answered blind.',
    layer: 'Bayesian probe',
  },
  {
    id: 's3-probe-evidence',
    snak: 3,
    title: 'Probe 2 — the evidence',
    purpose: 'Reader variability, and what identical rules every time actually buys.',
    layer: 'Bayesian probe',
  },
  {
    id: 's3-probe-update',
    snak: 3,
    title: 'Probe 3 — update',
    purpose: 'Would a consistent count change what you record? Peer responses shown.',
    layer: 'Bayesian probe',
  },
  {
    id: 's3-pilot',
    snak: 3,
    title: 'Pilot AI in routine practice',
    purpose: '50 scans, 8 weeks, anonymised — an empirical offer, not a claim.',
  },
  {
    id: 's3-predict',
    snak: 3,
    title: 'Differentiation · Predict',
    purpose: 'Fully branded and openly aspirational. Hypothesis, not claim.',
  },
  {
    id: 's3-cocreate',
    snak: 3,
    title: 'Co-create',
    purpose: 'The ask is participation: pilot, dataset, peer faculty.',
  },
]

export const storyboardMeta = {
  eyebrow: 'Internal review document',
  title: 'Storyboard — Precision phenotyping in DME',
  subtitle: 'Every page and every state · navigation, hidden layers and cluster views · for comment',
  navLabel: 'Navigation',
  navArchetype: 'Entry and modes',
  navGoal: 'How a specialist or a presenter enters the material.',
  reviewerFields: ['Reviewer', 'Function', 'Date'],
  commentLabel: 'Review comments',
  printLabel: 'Download PDF',
  printHint: 'Opens your print dialog — choose “Save as PDF”.',
  footer:
    'Internal use only. Company confidential. Not intended for external discussion or dissemination.',
}

export const journeyMeta = {
  title: 'The journey',
  subtitle: 'From revealing the phenotype to precision treatment',
  axisLabel: 'Precision medicine adoption',
  axisStart: 'Low',
  axisEnd: 'High',
  scienceLabel: 'Scientific weight',
  brandLabel: 'Brand presence',
  closing:
    'As evidence accumulates the trust requirement falls and brand presence rises. The design follows the same curve: monochrome and clinical at the start, colour-led and branded at the end.',
}
